import { useState, useMemo } from 'react';
import BackofficeLayout from '@/components/backoffice/BackofficeLayout';
import {
  comunicadosEnviados,
  tiposComunicado,
  publicosComunicado,
  type ComunicadoEnviado,
} from '@/mocks/backoffice/comunicados';
import { empresasPlataforma } from '@/mocks/backoffice/empresas';

type TipoComunicado = 'email' | 'notificacao' | 'sms' | 'push';
type PublicoComunicado = 'todos' | 'empresas' | 'participantes' | 'especifico';

export default function BackofficeComunicadosPage() {
  const [activeTab, setActiveTab] = useState<'novo' | 'historico'>('novo');
  const [titulo, setTitulo] = useState('');
  const [conteudo, setConteudo] = useState('');
  const [tipo, setTipo] = useState<TipoComunicado>('email');
  const [publico, setPublico] = useState<PublicoComunicado>('todos');
  const [empresasSelecionadas, setEmpresasSelecionadas] = useState<string[]>([]);
  const [agendar, setAgendar] = useState(false);
  const [dataAgendamento, setDataAgendamento] = useState('');
  const [comunicados, setComunicados] = useState<ComunicadoEnviado[]>([...comunicadosEnviados]);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [filtroTipo, setFiltroTipo] = useState<TipoComunicado | 'todos'>('todos');

  const mostrarToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const comunicadosFiltrados = useMemo(() => {
    if (filtroTipo === 'todos') return comunicados;
    return comunicados.filter((c) => c.tipo === filtroTipo);
  }, [comunicados, filtroTipo]);

  const handleEnviar = () => {
    if (!titulo.trim() || !conteudo.trim()) {
      mostrarToast('Preencha o título e o conteúdo do comunicado', 'error');
      return;
    }
    if (publico === 'especifico' && empresasSelecionadas.length === 0) {
      mostrarToast('Selecione pelo menos uma empresa', 'error');
      return;
    }

    const totalDest = publico === 'todos' ? 1884 : publico === 'participantes' ? 1872 : publico === 'empresas' ? 12 : empresasSelecionadas.length;
    const novo: ComunicadoEnviado = {
      id: `COM-${String(comunicados.length + 1).padStart(3, '0')}`,
      titulo: titulo.trim(),
      conteudo: conteudo.trim(),
      tipo,
      publico,
      empresasIds: publico === 'especifico' ? empresasSelecionadas : undefined,
      enviadoPor: 'admin@getpoints.com.br',
      dataEnvio: agendar && dataAgendamento ? new Date(dataAgendamento).toISOString() : new Date().toISOString(),
      totalDestinatarios: totalDest,
      totalEnviados: agendar ? 0 : totalDest,
      totalErros: 0,
      status: agendar ? 'agendado' : 'enviado',
      agendadoPara: agendar ? dataAgendamento : undefined,
    };

    setComunicados((prev) => [novo, ...prev]);
    setTitulo('');
    setConteudo('');
    setTipo('email');
    setPublico('todos');
    setEmpresasSelecionadas([]);
    setAgendar(false);
    setDataAgendamento('');
    mostrarToast(agendar ? 'Comunicado agendado com sucesso' : 'Comunicado enviado com sucesso', 'success');
    setActiveTab('historico');
  };

  const toggleEmpresa = (id: string) => {
    setEmpresasSelecionadas((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]
    );
  };

  const getTipoIcon = (t: string) => tiposComunicado.find((x) => x.id === t)?.icon || 'ri-mail-line';
  const getTipoLabel = (t: string) => tiposComunicado.find((x) => x.id === t)?.label || t;
  const getPublicoLabel = (p: string) => publicosComunicado.find((x) => x.id === p)?.label || p;

  const getStatusBadge = (s: string) => {
    switch (s) {
      case 'enviado': return 'bg-emerald-100 text-emerald-700';
      case 'enviando': return 'bg-blue-100 text-blue-700';
      case 'agendado': return 'bg-amber-100 text-amber-700';
      case 'cancelado': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl font-bold text-gray-800">Comunicados em Massa</h1>
          <p className="text-sm text-gray-500 mt-1">Envie notificações e comunicados para participantes e empresas</p>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab('novo')}
            className={`px-4 py-2 rounded-md text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap ${
              activeTab === 'novo' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-circle-line text-sm"></i>
              </div>
              <span>Novo Comunicado</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('historico')}
            className={`px-4 py-2 rounded-md text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap ${
              activeTab === 'historico' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-history-line text-sm"></i>
              </div>
              <span>Histórico ({comunicados.length})</span>
            </div>
          </button>
        </div>

        {/* Novo Comunicado */}
        {activeTab === 'novo' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Form */}
            <div className="lg:col-span-2 space-y-5">
              <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-5">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">Título do Comunicado</label>
                  <input
                    type="text"
                    value={titulo}
                    onChange={(e) => setTitulo(e.target.value)}
                    placeholder="Ex: Nova Campanha de Incentivo"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">
                    Conteúdo <span className="text-gray-400 font-normal">({conteudo.length}/2000)</span>
                  </label>
                  <textarea
                    value={conteudo}
                    onChange={(e) => setConteudo(e.target.value.slice(0, 2000))}
                    rows={8}
                    placeholder="Digite o conteúdo completo do comunicado..."
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Tipo de Envio</label>
                    <div className="grid grid-cols-2 gap-2">
                      {tiposComunicado.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => setTipo(t.id as TipoComunicado)}
                          className={`flex items-center space-x-2 px-3 py-2.5 rounded-lg text-xs font-bold transition-all duration-200 cursor-pointer border ${
                            tipo === t.id
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                              : 'border-gray-100 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className={`${t.icon} text-sm`}></i>
                          </div>
                          <span>{t.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Público-Alvo</label>
                    <div className="space-y-2">
                      {publicosComunicado.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => setPublico(p.id as PublicoComunicado)}
                          className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-xs font-bold transition-all duration-200 cursor-pointer border ${
                            publico === p.id
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                              : 'border-gray-100 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className={`${
                              p.id === 'todos' ? 'ri-global-line' :
                              p.id === 'empresas' ? 'ri-building-line' :
                              p.id === 'participantes' ? 'ri-user-line' :
                              'ri-checkbox-multiple-line'
                            } text-sm`}></i>
                          </div>
                          <span>{p.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                {publico === 'especifico' && (
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">
                      Selecionar Empresas ({empresasSelecionadas.length} selecionadas)
                    </label>
                    <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-lg p-2 space-y-1">
                      {empresasPlataforma.map((e) => (
                        <label
                          key={e.id}
                          className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={empresasSelecionadas.includes(e.id)}
                            onChange={() => toggleEmpresa(e.id)}
                            className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-gray-700 truncate">{e.nome_fantasia}</p>
                            <p className="text-xs text-gray-400">{e.cnpj}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="agendar"
                    checked={agendar}
                    onChange={(e) => setAgendar(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <label htmlFor="agendar" className="text-sm font-bold text-gray-700 cursor-pointer">
                    Agendar envio
                  </label>
                </div>
                {agendar && (
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Data e Hora</label>
                    <input
                      type="datetime-local"
                      value={dataAgendamento}
                      onChange={(e) => setDataAgendamento(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  </div>
                )}
                <div className="pt-2">
                  <button
                    onClick={handleEnviar}
                    className="w-full px-4 py-3 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-send-plane-line text-sm"></i>
                      </div>
                      <span>{agendar ? 'Agendar Comunicado' : 'Enviar Comunicado'}</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center space-x-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line text-sm text-gray-400"></i>
                  </div>
                  <span>Preview</span>
                </h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-stone-900 px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 rounded-full bg-emerald-600 flex items-center justify-center">
                        <span className="text-[8px] font-bold text-white">GP</span>
                      </div>
                      <span className="text-xs font-bold text-white">getPoints</span>
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    <p className="text-sm font-bold text-gray-800">
                      {titulo || 'Título do comunicado'}
                    </p>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      {conteudo || 'O conteúdo do comunicado aparecerá aqui...'}
                    </p>
                    <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                      <span className="text-[10px] text-gray-400">
                        {getTipoLabel(tipo)} &middot; {getPublicoLabel(publico)}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {new Date().toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="text-sm font-bold text-gray-700 mb-3">Resumo do Envio</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Destinatários</span>
                    <span className="font-bold text-gray-800">
                      {publico === 'todos' ? '1.884' : publico === 'participantes' ? '1.872' : publico === 'empresas' ? '12' : empresasSelecionadas.length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tipo</span>
                    <span className="font-bold text-gray-800">{getTipoLabel(tipo)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Público</span>
                    <span className="font-bold text-gray-800">{getPublicoLabel(publico)}</span>
                  </div>
                  {agendar && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Agendado para</span>
                      <span className="font-bold text-amber-600">
                        {dataAgendamento ? new Date(dataAgendamento).toLocaleString('pt-BR') : 'Não definido'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Histórico */}
        {activeTab === 'historico' && (
          <div className="space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center space-x-2">
                <select
                  value={filtroTipo}
                  onChange={(e) => setFiltroTipo(e.target.value as typeof filtroTipo)}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-white"
                >
                  <option value="todos">Todos os tipos</option>
                  {tiposComunicado.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/50">
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Comunicado</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Público</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Destinatários</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Data</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Enviado por</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comunicadosFiltrados.map((c) => (
                      <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-all duration-200">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-bold text-gray-800 text-sm">{c.titulo}</p>
                            <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[200px]">{c.conteudo}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-1.5">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className={`${getTipoIcon(c.tipo)} text-gray-400 text-xs`}></i>
                            </div>
                            <span className="text-xs text-gray-600">{getTipoLabel(c.tipo)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">{getPublicoLabel(c.publico)}</td>
                        <td className="px-4 py-3">
                          <div className="text-xs">
                            <span className="font-bold text-gray-800">{c.totalEnviados}</span>
                            <span className="text-gray-400"> / {c.totalDestinatarios}</span>
                            {c.totalErros > 0 && (
                              <span className="text-red-500 ml-1">({c.totalErros} erros)</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${getStatusBadge(c.status)}`}>
                            {c.status === 'enviado' ? 'Enviado' : c.status === 'enviando' ? 'Enviando' : c.status === 'agendado' ? 'Agendado' : 'Cancelado'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {new Date(c.dataEnvio).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-gray-500">{c.enviadoPor.split('@')[0]}</td>
                      </tr>
                    ))}
                    {comunicadosFiltrados.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-gray-400 text-sm">
                          Nenhum comunicado encontrado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-xl shadow-lg text-sm font-bold flex items-center space-x-2 z-50 ${
            toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-500 text-white'
          }`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`ri-${toast.type === 'success' ? 'check-line' : 'error-warning-line'} text-sm`}></i>
            </div>
            <span>{toast.message}</span>
          </div>
        )}
      </div>
    </BackofficeLayout>
  );
}